// src/modules/leads/pages/LeadWorkspace.jsx
// The Customer Representative's daily workspace: today's call queue + all my leads.
//
// This screen is used at least as much on a phone as on a desktop — a rep works
// the queue standing in a branch, not at a desk. A 10-column table inside a
// horizontal scroller is unusable there, so below `md` the same data renders as
// a list of cards where the two things a rep actually does (call, open) are
// full-size touch targets and nothing needs sideways scrolling.
import React, { useEffect, useState, useCallback } from 'react';
import {
  Card, Table, Tag, Typography, Row, Col, Statistic, Button, Tabs, Input, Space, message,
  Modal, Form, Select, Grid, List, Pagination, Empty,
} from 'antd';
import {
  PhoneOutlined, ReloadOutlined, ClockCircleOutlined, SearchOutlined, PlusOutlined,
  EnvironmentOutlined, CalendarOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useAuth } from '../../../context/AuthContext';
import { fetchMyQueue, fetchLeads, createLead, fetchLeadTypes, fetchLeadSources, fetchLeadStatuses } from '../api';
import { STATUS_OPTIONS, tagFor } from '../constants';
import LeadDetailPanel from '../components/LeadDetailPanel';
import CallButton from '../../callSync/components/CallButton';
import { useCallSession } from '../../callSync/context/CallSessionContext';

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

export default function LeadWorkspace() {
  const { user } = useAuth();
  const uid = user?.id || user?._id;
  const screens = useBreakpoint();
  // `md` is the point at which the full table stops fitting without a
  // horizontal scroller.
  const isMobile = !screens.md;

  const [queue, setQueue] = useState([]);
  const [mine, setMine] = useState([]);
  const [collected, setCollected] = useState([]);
  // Server-side paging: the tables page through the API instead of a capped one-shot fetch.
  const [minePage, setMinePage] = useState(1);
  const [minePageSize, setMinePageSize] = useState(20);
  const [mineTotal, setMineTotal] = useState(0);
  const [collectedPage, setCollectedPage] = useState(1);
  const [collectedPageSize, setCollectedPageSize] = useState(20);
  const [collectedTotal, setCollectedTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  // Lead status (Hot/Cold…), source and type — catalog codes, null = any.
  const [catFilters, setCatFilters] = useState({ interestLevel: null, source: null, leadType: null });
  const setCat = (key) => (v) => setCatFilters((f) => ({ ...f, [key]: v || null }));
  const [activeId, setActiveId] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const [leadTypes, setLeadTypes] = useState([]);
  const [leadSources, setLeadSources] = useState([]);
  const [leadStatuses, setLeadStatuses] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [addForm] = Form.useForm();
  const [saving, setSaving] = useState(false);

  const callSession = useCallSession();

  // code → { name, color } lookup for rendering the dynamic Status tag.
  const statusMap = Object.fromEntries(leadStatuses.map((s) => [s.code, s]));

  useEffect(() => {
    fetchLeadTypes().then((r) => setLeadTypes(r?.data?.data || [])).catch(() => {});
    fetchLeadSources().then((r) => setLeadSources(r?.data?.data || [])).catch(() => {});
    fetchLeadStatuses().then((r) => setLeadStatuses(r?.data?.data || [])).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const cat = Object.fromEntries(Object.entries(catFilters).filter(([, v]) => v));
      const [q, m, c] = await Promise.all([
        fetchMyQueue(),
        fetchLeads({ assignedTo: uid, page: minePage, limit: minePageSize, ...(search ? { search } : {}), ...cat }),
        fetchLeads({ assignedTo: uid, selfCollected: true, page: collectedPage, limit: collectedPageSize, ...cat }),
      ]);
      // The daily queue endpoint takes no filters, so narrow it here.
      setQueue((q.data.data || []).filter((l) => Object.entries(cat).every(([k, v]) => l[k] === v)));
      setMine(m.data.data || []);
      setCollected(c.data.data || []);
      setMineTotal(m.data.pagination?.total ?? (m.data.data || []).length);
      setCollectedTotal(c.data.pagination?.total ?? (c.data.data || []).length);
    } catch (err) {
      message.error(err?.response?.data?.message || 'Failed to load leads');
    } finally { setLoading(false); }
  }, [uid, search, catFilters, minePage, minePageSize, collectedPage, collectedPageSize]);

  // A new search starts from page 1, otherwise page 5 of the old result set is requested.
  useEffect(() => { setMinePage(1); setCollectedPage(1); }, [search, catFilters]);

  useEffect(() => { if (uid) load(); }, [uid, load]);

  // Any call finishing anywhere (including one resumed after a page reload)
  // changes a lead's attempt count and next-call date, so the lists refresh.
  useEffect(() => {
    if (callSession.lastFinishedAt) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callSession.lastFinishedAt]);

  const submitAdd = async () => {
    try {
      const v = await addForm.validateFields();
      setSaving(true);
      await createLead({
        ...v,
        isSelfCollected: true,
        assignedTo: uid,
      });
      message.success('Lead added to your collection');
      setAddOpen(false); addForm.resetFields();
      setCollectedPage(1);
      if (collectedPage === 1) load();
    } catch (err) {
      if (!err?.errorFields) message.error(err?.response?.data?.message || 'Failed to add lead');
    } finally { setSaving(false); }
  };

  const openLead = (id) => { setActiveId(id); setDrawerOpen(true); };

  const columns = [
    { title: 'Name', dataIndex: 'name', render: (v, r) => <a onClick={() => openLead(r._id)}>{v}</a> },
    { title: 'Phone', dataIndex: 'phone', render: (v, r) => <span>{v}{r.altPhone ? ` / ${r.altPhone}` : ''}</span> },
    // { title: 'Address', dataIndex: 'address', ellipsis: true, render: (v, r) => [v, r.city].filter(Boolean).join(', ') || '—' },
    { title: 'Type', dataIndex: 'leadType', render: (v) => v ? <Tag>{v}</Tag> : '—' },
    // { title: 'What customer says', dataIndex: 'lastRemark', ellipsis: true, render: (v) => v || <Text type="secondary">—</Text> },
    // { title: 'Next Call', dataIndex: 'nextCallDate', render: (v, r) => v ? <span style={{ color: r.isOverdue ? '#cf1322' : undefined }}>{dayjs(v).format('DD/MM/YY')}</span> : '—' },
    // { title: 'Visit', dataIndex: 'visitDate', render: (v) => v ? dayjs(v).format('DD/MM/YY') : '—' },
    { title: 'Status', dataIndex: 'interestLevel', render: (v) => v ? <Tag color={statusMap[v]?.color || 'default'}>{statusMap[v]?.name || v}</Tag> : '—' },
    { title: 'Stage', dataIndex: 'status', render: (v) => <Tag color={tagFor(STATUS_OPTIONS, v).color}>{tagFor(STATUS_OPTIONS, v).label}</Tag> },
    {
      title: '',
      // The old button was labelled "Call" but only opened the drawer. Now that
      // the CRM can genuinely dial, "Call" dials and "Open" shows the detail —
      // the drawer is still reachable from the name link too.
      render: (_, r) => (
        <Space size={4}>
          <CallButton lead={r} size="small" />
          <Button size="small" onClick={() => openLead(r._id)}>Open</Button>
        </Space>
      ),
    },
  ];

  /**
   * One lead as a card. Everything a rep needs to decide "call or skip" is
   * visible without a tap, and the two actions are thumb-sized.
   */
  const LeadCard = ({ lead }) => {
    const stage = tagFor(STATUS_OPTIONS, lead.status);
    const status = statusMap[lead.interestLevel];
    const address = [lead.address, lead.city].filter(Boolean).join(', ');
    return (
      <Card
        size="small"
        style={{ marginBottom: 10, borderColor: lead.isOverdue ? '#ffa39e' : undefined }}
        styles={{ body: { padding: 12 } }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'flex-start' }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <a onClick={() => openLead(lead._id)} style={{ fontWeight: 600, fontSize: 15 }}>{lead.name}</a>
            <div style={{ fontSize: 13 }}>
              <a href={`tel:${lead.phone}`} onClick={(e) => e.stopPropagation()}>{lead.phone}</a>
              {lead.altPhone ? <Text type="secondary"> / {lead.altPhone}</Text> : null}
            </div>
          </div>
          <Space size={4} wrap style={{ justifyContent: 'flex-end' }}>
            <Tag color={stage.color} style={{ marginInlineEnd: 0 }}>{stage.label}</Tag>
            {status && <Tag color={status.color || 'default'} style={{ marginInlineEnd: 0 }}>{status.name}</Tag>}
          </Space>
        </div>

        {address && (
          <div style={{ fontSize: 12, color: '#888', marginTop: 6 }}>
            <EnvironmentOutlined /> {address}
          </div>
        )}

        {lead.lastRemark && (
          <div style={{ fontSize: 13, marginTop: 6 }}>
            <Text type="secondary">Says: </Text>{lead.lastRemark}
          </div>
        )}

        {(lead.nextCallDate || lead.visitDate || lead.leadType) && (
          <div style={{ fontSize: 12, color: '#888', marginTop: 6, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            {lead.nextCallDate && (
              <span style={{ color: lead.isOverdue ? '#cf1322' : undefined }}>
                <CalendarOutlined /> Next {dayjs(lead.nextCallDate).format('DD/MM/YY')}
              </span>
            )}
            {lead.visitDate && <span>Visit {dayjs(lead.visitDate).format('DD/MM/YY')}</span>}
            {lead.leadType && <span>{lead.leadType}</span>}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
          <div style={{ flex: 1 }}>
            <CallButton lead={lead} block />
          </div>
          <Button style={{ flex: 1 }} onClick={() => openLead(lead._id)}>Open</Button>
        </div>
      </Card>
    );
  };

  /**
   * The mobile equivalent of a Table: same data, same paging contract, no
   * horizontal scrolling.
   */
  const LeadCardList = ({ data, emptyText, pagination }) => (
    <>
      {!data.length && !loading ? (
        <Empty description={emptyText} image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ padding: '24px 0' }} />
      ) : (
        <List
          loading={loading}
          dataSource={data}
          split={false}
          renderItem={(lead) => <LeadCard key={lead._id} lead={lead} />}
        />
      )}
      {pagination && pagination.total > pagination.pageSize && (
        <div style={{ textAlign: 'center', marginTop: 8 }}>
          <Pagination
            simple
            size="small"
            current={pagination.current}
            pageSize={pagination.pageSize}
            total={pagination.total}
            onChange={pagination.onChange}
          />
        </div>
      )}
    </>
  );

  const overdue = queue.filter((l) => l.isOverdue).length;
  const pendingToday = queue.filter((l) => !l.contactedToday).length;

  const tabPane = (data, { emptyText, pagination, tablePagination }) => (isMobile
    ? <LeadCardList data={data} emptyText={emptyText} pagination={pagination} />
    : (
      <Table
        rowKey="_id" size="small" columns={columns} dataSource={data} loading={loading}
        pagination={tablePagination}
        onChange={pagination ? (pg) => pagination.onChange(pg.current, pg.pageSize) : undefined}
        locale={{ emptyText }}
        scroll={{ x: 1000 }}
      />
    ));

  return (
    <div style={{ padding: '8px 0' }}>
      {/* The header stacks on a phone: a 220px search box next to three buttons
          overflows every handset otherwise. */}
      <div style={{ marginBottom: 12 }}>
        <Title level={4} style={{ margin: '0 0 8px' }}><PhoneOutlined /> My Leads</Title>
        <Row gutter={[8, 8]} align="middle">
          <Col xs={24} sm={12} md={10} lg={8}>
            <Input
              allowClear prefix={<SearchOutlined />} placeholder="Search name / phone" value={search}
              onChange={(e) => setSearch(e.target.value)} onPressEnter={load} style={{ width: '100%' }}
            />
          </Col>
          <Col xs={12} sm={6} md={5} lg={4}>
            <Button icon={<ReloadOutlined />} onClick={load} loading={loading} block>Refresh</Button>
          </Col>
          <Col xs={12} sm={6} md={5} lg={4}>
            <Button type="primary" icon={<PlusOutlined />} block
              onClick={() => { addForm.resetFields(); setAddOpen(true); }}>Add Lead</Button>
          </Col>
        </Row>
        <Row gutter={[8, 8]} style={{ marginTop: 8 }}>
          <Col xs={24} sm={8} md={6} lg={5}>
            <Select allowClear placeholder="Lead status" style={{ width: '100%' }} value={catFilters.interestLevel}
              onChange={setCat('interestLevel')}
              options={leadStatuses.map((s) => ({ value: s.code, label: <Tag color={s.color || 'default'}>{s.name}</Tag> }))} />
          </Col>
          <Col xs={12} sm={8} md={6} lg={5}>
            <Select allowClear placeholder="Lead source" style={{ width: '100%' }} value={catFilters.source}
              onChange={setCat('source')} options={leadSources.map((s) => ({ value: s.code, label: s.name }))} />
          </Col>
          <Col xs={12} sm={8} md={6} lg={5}>
            <Select allowClear placeholder="Lead type" style={{ width: '100%' }} value={catFilters.leadType}
              onChange={setCat('leadType')} options={leadTypes.map((t) => ({ value: t.code, label: t.name }))} />
          </Col>
        </Row>
      </div>

      <Row gutter={[8, 8]} style={{ marginBottom: 16 }}>
        <Col xs={12} sm={6}><Card size="small"><Statistic title="Today's Queue" value={queue.length} prefix={<ClockCircleOutlined />} /></Card></Col>
        <Col xs={12} sm={6}><Card size="small"><Statistic title="Pending Today" value={pendingToday} valueStyle={{ color: '#fa8c16' }} /></Card></Col>
        <Col xs={12} sm={6}><Card size="small"><Statistic title="Overdue" value={overdue} valueStyle={{ color: '#cf1322' }} /></Card></Col>
        <Col xs={12} sm={6}><Card size="small"><Statistic title="Total Assigned" value={mineTotal} /></Card></Col>
      </Row>

      <Tabs
        defaultActiveKey="today"
        size={isMobile ? 'small' : 'middle'}
        // Three labels with counts do not fit across a phone; swiping the tab
        // strip beats truncating the labels.
        tabBarGutter={isMobile ? 12 : undefined}
        items={[
          {
            key: 'today',
            label: isMobile ? `Today (${queue.length})` : `Today's Calls (${queue.length})`,
            children: tabPane(queue, {
              emptyText: 'No calls due today 🎉',
              tablePagination: { pageSize: 20 },
            }),
          },
          {
            key: 'all',
            label: isMobile ? `All (${mineTotal})` : `All My Leads (${mineTotal})`,
            children: tabPane(mine, {
              emptyText: 'No leads assigned',
              pagination: {
                current: minePage,
                pageSize: minePageSize,
                total: mineTotal,
                onChange: (page, size) => { setMinePage(page); setMinePageSize(size); },
              },
              tablePagination: {
                current: minePage, pageSize: minePageSize, total: mineTotal,
                showSizeChanger: true, pageSizeOptions: [20, 50, 100, 200], showTotal: (t) => `${t} leads`,
              },
            }),
          },
          {
            key: 'collected',
            label: isMobile ? `Collected (${collectedTotal})` : `Lead Collection (${collectedTotal})`,
            children: tabPane(collected, {
              emptyText: 'No self-collected leads yet — use "Add Lead" for leads you found outside the assigned pool',
              pagination: {
                current: collectedPage,
                pageSize: collectedPageSize,
                total: collectedTotal,
                onChange: (page, size) => { setCollectedPage(page); setCollectedPageSize(size); },
              },
              tablePagination: {
                current: collectedPage, pageSize: collectedPageSize, total: collectedTotal,
                showSizeChanger: true, pageSizeOptions: [20, 50, 100, 200], showTotal: (t) => `${t} leads`,
              },
            }),
          },
        ]}
      />

      <LeadDetailPanel leadId={activeId} open={drawerOpen} onClose={() => setDrawerOpen(false)} onChanged={load} />

      <Modal title="Add a Self-Collected Lead" open={addOpen} onCancel={() => setAddOpen(false)}
        onOk={submitAdd} okButtonProps={{ loading: saving }} destroyOnClose
        // On a phone a fixed 520px dialog is wider than the viewport.
        width={isMobile ? '100%' : 520}
        style={isMobile ? { top: 8, maxWidth: 'calc(100vw - 16px)', margin: '0 auto' } : undefined}
      >
        <Form form={addForm} layout="vertical">
          <Row gutter={12}>
            <Col xs={24} sm={12}><Form.Item name="name" label="Name" rules={[{ required: true }]}><Input /></Form.Item></Col>
            <Col xs={24} sm={12}><Form.Item name="phone" label="Phone" rules={[{ required: true }]}><Input inputMode="tel" /></Form.Item></Col>
            <Col xs={24} sm={12}><Form.Item name="altPhone" label="Alternate Phone"><Input inputMode="tel" /></Form.Item></Col>
            <Col xs={24} sm={12}><Form.Item name="leadType" label="Lead Type"><Select allowClear placeholder="Select type" options={leadTypes.map((t) => ({ value: t.code, label: t.name }))} /></Form.Item></Col>
            <Col xs={24} sm={12}><Form.Item name="source" label="Source"><Select allowClear placeholder="Select source" options={leadSources.map((s) => ({ value: s.code, label: s.name }))} /></Form.Item></Col>
            <Col xs={24} sm={12}><Form.Item name="interestLevel" label="Status"><Select allowClear placeholder="Hot / Cold / …" options={leadStatuses.map((s) => ({ value: s.code, label: <Tag color={s.color || 'default'}>{s.name}</Tag> }))} /></Form.Item></Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
}
